/**
 * 路由配置主文件
 *
 * 支持租户端和平台管理端两种布局
 * - 租户端：使用 TenantLayout，路径为 /tenant/*
 * - 平台端：使用 PlatformLayout，路径为 /platform/*（仅 SaaS 模式）
 */

import React, { Suspense, useMemo } from 'react';
import { createBrowserRouter, RouterProvider, Navigate, useRouteError, type RouteObject } from 'react-router-dom';

import { staticRoutes } from './staticRoutes';
import { tenantRoutes } from './tenantRoutes';
import AuthRoute from './guards/AuthRoute';
import { isStaleAssetError, reloadForFreshAssets } from '@/utils/staleAssetReload';

import { platformRoutes as _platformRoutes } from './platformRoutes';

function RouterErrorPage() {
  const error = useRouteError();
  if (isStaleAssetError(error)) {
    reloadForFreshAssets();
    return null;
  }
  const msg = error instanceof Error ? error.message : String(error ?? '未知错误');
  return (
    <div style={{ padding: 40, textAlign: 'center' }}>
      <h2>页面加载失败</h2>
      <p style={{ color: '#999', marginBottom: 16 }}>{msg}</p>
      <button onClick={() => window.location.reload()} style={{ padding: '8px 24px', cursor: 'pointer' }}>
        刷新页面
      </button>
    </div>
  );
}

/**
 * 路由配置生成函数
 * 支持租户端和平台管理端两种布局
 */
export const createAppRouter = () => {
  // 平台管理路由（AuthRoute 校验 platform_admin 角色）
  const platformRoute: RouteObject[] = [{
    ..._platformRoutes,
    element: (
      <AuthRoute requiredRole="platform_admin">
        {_platformRoutes.element}
      </AuthRoute>
    ),
  }];

  return createBrowserRouter([
    // 静态路由（登录、异常页面等）
    ...staticRoutes.map(route => ({
      ...route,
      element: route.element,
      errorElement: <RouterErrorPage />,
    })),

    // 租户路由（使用 TenantLayout）
    {
      ...tenantRoutes,
      errorElement: <RouterErrorPage />,
      element: (
        <AuthRoute>
          {tenantRoutes.element}
        </AuthRoute>
      ),
    },

    // 平台管理路由（仅 SaaS 模式，使用 PlatformLayout）
    ...platformRoute.map(r => ({ ...r, errorElement: <RouterErrorPage /> })),

    // 根路径重定向到租户工作台
    {
      path: '/',
      element: <Navigate to="/tenant/dashboard" replace />,
    },

    // 兼容旧的 /dashboard 路径，重定向到租户工作台
    {
      path: '/dashboard',
      element: <Navigate to="/tenant/dashboard" replace />,
    },
    {
      path: '/dashboard/*',
      element: <Navigate to="/tenant/dashboard" replace />,
    },

    // 兼容旧的管理后台入口，避免登录/门户选择旧链接落到 404
    {
      path: '/admin',
      element: <Navigate to="/tenant/admin/dashboard" replace />,
    },
    {
      path: '/admin/dashboard',
      element: <Navigate to="/tenant/admin/dashboard" replace />,
    },
    {
      path: '/admin/*',
      element: <Navigate to="/tenant/admin/dashboard" replace />,
    },

    // 404 路由（放在最后）
    {
      path: '*',
      element: <Navigate to="/exception/404" replace />,
    },
  ]);
};

/**
 * 路由渲染组件
 * 纯租户端系统，使用静态路由配置
 */
const RouterRenderer: React.FC = () => {
  // 创建路由器（静态配置，不需要动态加载）
  const router = useMemo(() => {
    console.log('🔄 创建路由器');
    return createAppRouter();
  }, []);

  // 直接渲染路由
  return (
    <Suspense fallback={
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        background: '#f0f2f5'
      }}>
        <div>页面加载中...</div>
      </div>
    }>
      <RouterProvider router={router} future={{ v7_startTransition: true }} />
    </Suspense>
  );
};

/**
 * 应用路由组件
 */
const AppRouter: React.FC = () => {
  return <RouterRenderer />;
};

// 默认导出
export default AppRouter;
